/**
 * Per-player settings — one modal with collapsible sections for Account,
 * Card style, and Card back. Sections collapse so the sheet stays tight
 * even as we add more preferences.
 *
 * Account section claims the current guest identity on signup, rebinds
 * the cookie on login, and offers logout for existing accounts. Card
 * pickers update the player record server-side and live-preview the
 * change against the user's other current pref.
 */

import { useState, type ReactNode } from "react";
import {
  CARD_BACKS,
  CARD_STYLES,
  CardBackProvider,
  CardStyleProvider,
} from "./cardThemes.js";
import { PlayingCard } from "./PlayingCard.js";
import type {
  ApiAuthResult,
  CardBackId,
  CardThemeId,
  PlayerIdentity,
} from "../../../shared/protocol.js";

export interface SettingsSheetProps {
  me: PlayerIdentity | null;
  styleId: CardThemeId;
  backId: CardBackId;
  onClose: () => void;
  onSetStyle: (style: CardThemeId) => Promise<void>;
  onSetBack: (back: CardBackId) => Promise<void>;
  onSignup: (username: string, password: string) => Promise<ApiAuthResult>;
  onLogin: (username: string, password: string) => Promise<ApiAuthResult>;
  onLogout: () => Promise<ApiAuthResult>;
}

const STYLE_PREVIEW_CARDS = ["AH", "KS", "TD", "5C"] as const;
type SectionId = "account" | "style" | "back";

export function SettingsSheet({
  me,
  styleId,
  backId,
  onClose,
  onSetStyle,
  onSetBack,
  onSignup,
  onLogin,
  onLogout,
}: SettingsSheetProps) {
  // Default open section: account if logged out (priming the signup CTA),
  // card style otherwise (the most common reason to open Settings).
  const [open, setOpen] = useState<SectionId | null>(
    me?.hasPassword ? "style" : "account"
  );
  function toggle(id: SectionId) {
    setOpen((cur) => (cur === id ? null : id));
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet settings-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Settings"
      >
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Settings</h2>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-content settings-accordion">
          <Section
            id="account"
            title="Account"
            subtitle={
              me?.hasPassword
                ? `Signed in as @${me.username}`
                : "Sign in or create an account"
            }
            open={open === "account"}
            onToggle={() => toggle("account")}
          >
            <AccountPanel
              me={me}
              onSignup={onSignup}
              onLogin={onLogin}
              onLogout={onLogout}
            />
          </Section>

          <Section
            id="style"
            title="Card style"
            subtitle={CARD_STYLES[styleId].label}
            open={open === "style"}
            onToggle={() => toggle("style")}
          >
            <p className="muted settings-section-desc" style={{ marginTop: 0 }}>
              Color and weight of the card faces. Your preference is saved and
              only changes what you see.
            </p>
            <div className="theme-grid" role="radiogroup" aria-label="Card style">
              {(Object.keys(CARD_STYLES) as CardThemeId[]).map((id) => {
                const t = CARD_STYLES[id];
                const selected = id === styleId;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`theme-card${selected ? " selected" : ""}`}
                    onClick={() => { if (!selected) void onSetStyle(id); }}
                  >
                    <CardStyleProvider styleId={id}>
                      <div className="theme-preview">
                        {STYLE_PREVIEW_CARDS.map((c, i) => (
                          <PlayingCard key={i} card={c} width={42} />
                        ))}
                      </div>
                    </CardStyleProvider>
                    <div className="theme-card-meta">
                      <div className="theme-card-title">
                        {t.label}
                        {selected && <span className="theme-card-active">active</span>}
                      </div>
                      <div className="theme-card-desc">{t.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          <Section
            id="back"
            title="Card back"
            subtitle={CARD_BACKS[backId].label}
            open={open === "back"}
            onToggle={() => toggle("back")}
          >
            <p className="muted settings-section-desc" style={{ marginTop: 0 }}>
              The design shown for face-down cards and the deck pile.
            </p>
            <div className="back-grid" role="radiogroup" aria-label="Card back">
              {(Object.keys(CARD_BACKS) as CardBackId[]).map((id) => {
                const b = CARD_BACKS[id];
                const selected = id === backId;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`back-card${selected ? " selected" : ""}`}
                    onClick={() => { if (!selected) void onSetBack(id); }}
                  >
                    <CardBackProvider backId={id}>
                      <div className="back-preview">
                        <PlayingCard card={null} faceDown width={52} />
                      </div>
                    </CardBackProvider>
                    <div className="back-card-meta">
                      {b.label}
                      {selected && <span className="theme-card-active">active</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible section shell
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  id: SectionId;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`settings-section${open ? " open" : ""}`}>
      <button
        type="button"
        className="settings-section-header"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`section-${id}`}
      >
        <div className="settings-section-title">
          <h3>{title}</h3>
          <span className="muted settings-section-subtitle">{subtitle}</span>
        </div>
        <span className="settings-section-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div id={`section-${id}`} className="settings-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account section
// ---------------------------------------------------------------------------

type AuthTab = "login" | "signup";

function AccountPanel({
  me,
  onSignup,
  onLogin,
  onLogout,
}: {
  me: PlayerIdentity | null;
  onSignup: (username: string, password: string) => Promise<ApiAuthResult>;
  onLogin: (username: string, password: string) => Promise<ApiAuthResult>;
  onLogout: () => Promise<ApiAuthResult>;
}) {
  const loggedIn = me?.hasPassword === true;
  const [tab, setTab] = useState<AuthTab>("signup");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const r = tab === "signup"
        ? await onSignup(username, password)
        : await onLogin(username, password);
      setSuccess(r.message ?? (tab === "signup" ? "Account created." : "Logged in."));
      setUsername("");
      setPassword("");
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await onLogout();
      setSuccess(r.message ?? "Logged out.");
    } catch (err: any) {
      setError(err?.message ?? "Logout failed");
    } finally {
      setBusy(false);
    }
  }

  if (loggedIn) {
    return (
      <div className="account-summary">
        <div>
          <p className="muted" style={{ marginTop: 0, marginBottom: 4 }}>Signed in as</p>
          <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>@{me?.username}</p>
          <p className="muted" style={{ marginTop: 4, marginBottom: 0, fontSize: 13 }}>
            Display name: <strong>{me?.displayName}</strong>{" "}
            <span className="muted">(editable from the topbar)</span>
          </p>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>
          Your username is the stable identifier across browsers and devices.
          Display names can change any time without losing history.
        </p>
        <button onClick={logout} disabled={busy}>
          {busy ? "…" : "Log out"}
        </button>
        {error && <div className="modal-error">{error}</div>}
        {success && <div className="modal-success">{success}</div>}
      </div>
    );
  }

  return (
    <>
      <div className="modal-tabs">
        <button
          className={`modal-tab${tab === "signup" ? " active" : ""}`}
          onClick={() => setTab("signup")}
          type="button"
        >
          Create account
        </button>
        <button
          className={`modal-tab${tab === "login" ? " active" : ""}`}
          onClick={() => setTab("login")}
          type="button"
        >
          Log in
        </button>
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        {tab === "signup"
          ? "Lock in your username so you can play from anywhere with the same identity. Creating an account keeps your current display name and history."
          : "Sign in to switch this browser to an existing account."}
      </p>
      <form className="account-form" onSubmit={submit}>
        <label>
          <span className="muted">Username</span>
          <input
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="3-24 chars, letters / numbers / underscore"
            aria-label="Username"
          />
        </label>
        <label>
          <span className="muted">Password</span>
          <input
            type="password"
            autoComplete={tab === "signup" ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={tab === "signup" ? "At least 8 characters" : ""}
            aria-label="Password"
          />
        </label>
        <button
          type="submit"
          className="primary"
          disabled={busy || !username || !password}
        >
          {busy ? "…" : tab === "signup" ? "Create account" : "Log in"}
        </button>
      </form>
      <p className="muted" style={{ fontSize: 11, marginTop: 12 }}>
        We never email you — there's nowhere to send a recovery link. Pick a
        password you'll remember.
      </p>
      {error && <div className="modal-error">{error}</div>}
      {success && <div className="modal-success">{success}</div>}
    </>
  );
}
